import { useState, useEffect } from 'react';

type UseCarouselProps = {
  viewportRef: React.RefObject<HTMLDivElement>;
};

type UseCarouselReturn = {
  currentIndex: number;
  handlePrevious: () => void;
  handleNext: () => void;
  handleScroll: (index: number) => void;
};

export const useCarousel = ({ viewportRef }: UseCarouselProps): UseCarouselReturn => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = Number(entry.target.getAttribute('data-index') ?? 0);
            setCurrentIndex(index);
          }
        });
      },
      {
        root: viewport,
        threshold: 0.5,
      }
    );

    Array.from(viewport.children).forEach((group) => {
      observer.observe(group);
    });

    return () => observer.disconnect();
  }, [viewportRef]);

  const handleScroll = (index: number): void => {
    const viewport = viewportRef.current;
    const targetElement = viewport?.querySelector(`[data-index="${index}"]`);
    if (viewport && targetElement) {
      viewport.scrollTo({
        left: viewport.scrollLeft + targetElement.getBoundingClientRect().left - viewport.getBoundingClientRect().left,
        behavior: 'smooth',
      });
    }
  };

  const handlePrevious = (): void => {
    const newIndex = Math.max(0, currentIndex - 1);
    handleScroll(newIndex);
  };

  const handleNext = (): void => {
    const numberOfItems = viewportRef.current?.children.length ?? 0;
    const newIndex = Math.min(numberOfItems - 1, currentIndex + 1);
    handleScroll(newIndex);
  };

  return { currentIndex, handlePrevious, handleNext, handleScroll };
};
